// ============================================================
// SEC-REQ-2: Sanitize bot-provided action parameters.
// Strip HTML tags, validate GUID format, enforce max lengths.
// ============================================================

/** UUID v4 regex pattern */
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Strip HTML tags from a string — SEC-REQ-2 + SEC-REQ-3 */
export const stripHtmlTags = (input: string): string => {
  if (!input || typeof input !== "string") return "";
  // Replace HTML tags with empty string
  return input.replace(/<[^>]*>/g, "").trim();
};

/** Validate that a string is a valid GUID/UUID format */
export const isValidGuid = (value: string): boolean => {
  if (!value || typeof value !== "string") return false;
  return GUID_REGEX.test(value);
};

/** Enforce max length on a string, truncating if necessary */
export const enforceMaxLength = (value: string, maxLength: number): string => {
  if (!value || typeof value !== "string") return "";
  return value.length > maxLength ? value.slice(0, maxLength) : value;
};

/** Max length constraints matching backend DTO */
const MAX_LENGTHS: Record<string, number> = {
  title: 100,
  description: 4000,
  notes: 4000,
  affected_machine_part: 100,
  alarm_code: 100,
  severity: 50,
};

/**
 * Sanitize all action parameters.
 * - Strip HTML from all string values
 * - Validate GUID format for *_id fields
 * - Enforce max lengths per field
 *
 * Returns sanitized copy of values + list of validation errors.
 */
export const sanitizeActionParams = (
  values: Record<string, any>
): { sanitized: Record<string, any>; errors: string[] } => {
  const sanitized: Record<string, any> = {};
  const errors: string[] = [];

  for (const [key, value] of Object.entries(values)) {
    if (value === null || value === undefined) {
      sanitized[key] = value;
      continue;
    }

    if (typeof value === "string") {
      // Strip HTML tags from all string values
      let clean = stripHtmlTags(value);

      // Validate GUID format for *_id fields
      if (key.endsWith("_id") && clean.length > 0) {
        if (!isValidGuid(clean)) {
          errors.push(`Invalid ID format for ${key}`);
          continue; // Skip invalid GUIDs
        }
      }

      // Enforce max length if known
      const maxLen = MAX_LENGTHS[key];
      if (maxLen && clean.length > maxLen) {
        clean = enforceMaxLength(clean, maxLen);
      }

      sanitized[key] = clean;
    } else if (typeof value === "number" || typeof value === "boolean") {
      sanitized[key] = value;
    } else if (Array.isArray(value)) {
      // Sanitize array elements (e.g., file references)
      sanitized[key] = value;
    } else {
      // Skip unknown complex types
      sanitized[key] = value;
    }
  }

  return { sanitized, errors };
};
